import { useEffect, useMemo, useRef, useState } from '../lib/teact/teact';

import type { ApiWalletMetadataDirectory } from '../api/types';
import type { Account } from '../global/types';
import type { WalletMetadataDirectoryChange } from '../util/walletMetadataDirectory';

import { parseAccountId } from '../util/account';
import { logDebugError } from '../util/logs';
import { WALLET_METADATA_DIRECTORY_CHANGED_EVENT } from '../util/walletMetadataDirectory';
import { trackWalletMetadataSync } from '../util/walletTelemetry';
import { callApiWithThrow } from '../api';
import useLastCallback from './useLastCallback';

const EMPTY_DIRECTORY: ApiWalletMetadataDirectory = { counterparties: [], categories: [] };
export { notifyWalletMetadataDirectoryChanged } from '../util/walletMetadataDirectory';

export default function useWalletMetadataDirectory(
  accountId: string,
  accounts: Record<string, Account> | undefined,
  isActive = true,
  enclaveToken?: string,
) {
  const [state, setState] = useState({ key: '', directory: EMPTY_DIRECTORY, isLoading: false });
  const requestRef = useRef(0);
  const retryTimerRef = useRef<number>();
  const retryDelayRef = useRef(1_000);
  const activeRequestKeyRef = useRef('');
  const accountsKey = useMemo(() => JSON.stringify(Object.entries(accounts ?? {})
    .filter(([id, account]) => account.type === 'mnemonic' && !account.isTemporary && !account.isRecoveryRequired
      && account.byChain.tron
      && parseAccountId(id).network === parseAccountId(accountId).network)
    .map(([id, account]) => [id, account.byChain.tron!.address]).sort()), [accountId, accounts]);
  const accountIds = useMemo(() => (JSON.parse(accountsKey) as [string, string][]).map(([id]) => id), [accountsKey]);
  const requestKey = `${accountId}:${accounts?.[accountId]?.byChain.tron?.address ?? ''}:${accountsKey}`;
  activeRequestKeyRef.current = requestKey;

  const reload = useLastCallback(async (shouldSync = false) => {
    if (!isActive || !accountIds.includes(accountId)) return;
    window.clearTimeout(retryTimerRef.current);
    retryTimerRef.current = undefined;
    const revision = ++requestRef.current;
    let hasPendingWork = false;
    setState((previous) => ({ ...previous, isLoading: true }));
    const update = (directory: ApiWalletMetadataDirectory) => {
      if (revision === requestRef.current) setState({ key: requestKey, directory, isLoading: false });
    };
    const sync = async () => {
      const startedAt = Date.now();
      try {
        const response = await callApiWithThrow('syncWalletMetadataDirectory', [accountId], enclaveToken);
        const result = response ?? { synced: 0, pending: 0 };
        hasPendingWork = Boolean(result.pending || result.mergesPending || result.mergesFailed);
        const superseded = revision !== requestRef.current || activeRequestKeyRef.current !== requestKey;
        trackWalletMetadataSync(superseded ? 'superseded' : hasPendingWork ? 'pending' : 'complete',
          result, Date.now() - startedAt);
        return result;
      } catch (error) {
        trackWalletMetadataSync('failed', {}, Date.now() - startedAt);
        throw error;
      }
    };
    try {
      update(await callApiWithThrow('fetchWalletMetadataDirectory', [accountId], true));
      if (revision !== requestRef.current) return;
      if (shouldSync) await sync();
      if (revision !== requestRef.current) return;
      update(await callApiWithThrow('fetchWalletMetadataDirectory', [accountId]));
      if (!shouldSync || revision !== requestRef.current) return;
      // Discovery can migrate pending records into a canonical shared book.
      await sync();
      if (revision !== requestRef.current) return;
      update(await callApiWithThrow('fetchWalletMetadataDirectory', [accountId], true));
    } catch (error) {
      hasPendingWork = shouldSync;
      logDebugError('Wallet metadata directory sync', error);
    } finally {
      if (revision === requestRef.current) {
        setState((previous) => ({ ...previous, isLoading: false }));
        if (shouldSync && hasPendingWork) {
          retryTimerRef.current = window.setTimeout(() => void reload(true), retryDelayRef.current);
          retryDelayRef.current = Math.min(retryDelayRef.current * 3, 30_000);
        } else retryDelayRef.current = 1_000;
      }
    }
  });

  useEffect(() => {
    if (!isActive) return undefined;
    retryDelayRef.current = 1_000;
    void reload(true);
    const refresh = (event?: Event) => {
      const change = (event as CustomEvent<WalletMetadataDirectoryChange | undefined>)?.detail;
      if (change?.accountId === accountId) {
        setState((previous) => {
          const directory = previous.key === requestKey ? previous.directory : EMPTY_DIRECTORY;
          if (change.record) {
            const key = 'emoji' in change.record ? 'categories' : 'counterparties';
            const records = directory[key];
            const next = [...records.filter(({ id }) => id !== change.record!.id), change.record] as typeof records;
            return { ...previous, key: requestKey, directory: { ...directory, [key]: next } };
          }
          if (change.removed) {
            const key = change.removed.kind === 'category' ? 'categories' : 'counterparties';
            return { ...previous, key: requestKey, directory: {
              ...directory, [key]: directory[key].filter(({ id }) => id !== change.removed!.id),
            } };
          }
          return previous;
        });
      }
      void reload(true);
    };
    window.addEventListener(WALLET_METADATA_DIRECTORY_CHANGED_EVENT, refresh);
    window.addEventListener('online', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      requestRef.current += 1;
      window.clearTimeout(retryTimerRef.current);
      retryTimerRef.current = undefined;
      window.removeEventListener(WALLET_METADATA_DIRECTORY_CHANGED_EVENT, refresh);
      window.removeEventListener('online', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [requestKey, isActive, enclaveToken, accountId]);

  return {
    ...(state.key === requestKey ? state.directory : EMPTY_DIRECTORY),
    accountIds,
    isLoading: Boolean(isActive && accountIds.includes(accountId) && state.isLoading),
    reload,
  };
}
